import { createModel } from 'models/BaseModel';
import {
  getLevelDiscountList,
  getLevelDiscount,
  updateLevelDiscount,
  updateLevelBenefits,
} from 'services/LevelAPIHelper';
import { DISPLAY_ONLINE_STORE_DISCOUNT_TYPE, LanguageConfig, ONLINE_STORE_DISCOUNT_TYPE, SESSION_KEYS } from 'config/CustomEnums';
import { getDetailPageDisplayLastModifiedDate, getListDisplayLastModifiedDate } from 'utils/TimeFormatUtil';
import { getDisplayLastModifiedBy } from 'utils';
import { cleanTranslations } from './LevelModel';
import { apiWithResponseHandle } from './LoadingUtil';

const parseSingleLevelBenefit = (item) => {
  const translationsEdges = item.translations?.edges;
  let translations = {};
  translations[LanguageConfig.english] = {
    language: LanguageConfig.english,
    title: item.title,
    content: item.content,
  }
  if (translationsEdges?.length > 0) {
    translationsEdges.forEach((item) => {
      const node = item.node;
      translations[node.language] = node;
    });
  }

  return {
    id: item.pk,
    pk: item.pk,
    title: item.title,
    content: item.content,
    highlight: item.highlight,
    displayOrder: item.displayOrder,
    translations,
  };
};

const parseLevelBenefits = (data) => {
  if (!data) {
    return [];
  };
  return data.map((item) => {
    return parseSingleLevelBenefit(item);
  });
}

const cleanLevelBenefits = (data) => {
  if (!data) {
    return [];
  }

  return data?.map((item, index) => {
    const formattedTranslation = cleanTranslations(
      item,
      LanguageConfig.english,
      ['title', 'content']
    );

    const otherTranslations = [];
    Object.keys(item?.translations || {}).forEach((language) => {
      if (language === LanguageConfig.english) {
        return;
      }
      otherTranslations.push(
        cleanTranslations(item, language, ['title', 'content']),
      );
    });

    const result = {
      highlight: item.highlight,
      displayOrder: index + 1,
      translations: otherTranslations,
      ...formattedTranslation,
    }

    if (item.pk) {
      result["id"] = item.pk;
    }

    return result;
  })
};

const parseDetail = (data) => {
  const discountCombinationFieldMap = {
    combinedWithOrderDiscount: ONLINE_STORE_DISCOUNT_TYPE.ORDER,
    combinedWithProductDiscount: ONLINE_STORE_DISCOUNT_TYPE.PRODUCT,
    combinedWithShippingDiscount: ONLINE_STORE_DISCOUNT_TYPE.DELIVERY,
  }

  const discountCombination = []
  Object.entries(discountCombinationFieldMap).forEach(([key, value]) => {
    if (data?.[key]) {
      discountCombination.push({
        pk: value,
        name: DISPLAY_ONLINE_STORE_DISCOUNT_TYPE?.[value] || value,
      })
    }
  })

  const parsedData = {
    ...data,
    onlineStoreDiscountType: data?.discountType,
    displayOnlineStoreDiscountType: DISPLAY_ONLINE_STORE_DISCOUNT_TYPE?.[data?.discountType] || data?.discountType,
    limitedMinSpending: data?.limitedMinSpend,
    limitedMinQuantity: data?.limitedMinQuantity,
    limitedCollections: (JSON.parse(data?.limitedCollections || '[]') || [])?.map(item => ({
      label: item?.title,
      value: {
        pk: item?.id,
        title: item?.title,
      }
    })),
    limitedProductTags: data.limitedProductTags?.map(
      item => ({
        label: item,
        value: {
          pk: item, name: item,
        }
      })),
    discountCombination,
    sourceName: data.level.levelName,
    name: data.level.levelName,
    displayDiscount: `${Number(data.discount) * 100}%`,
    displayShopifyFreeDelivery: data.freeDeliveryOnShopify ? 'Yes' : 'No',
    discountNumber: Number(data.discount) * 100,
    listDisplayLastModifiedTime: getListDisplayLastModifiedDate(data?.lastModifiedDate),
    detailDisplayCreationDate: getDetailPageDisplayLastModifiedDate(data?.creationDate),
    detailDisplayLastModifiedTime: getDetailPageDisplayLastModifiedDate(data?.lastModifiedDate),
    displayLastModifiedBy: getDisplayLastModifiedBy(data),
    levelBenefits: parseLevelBenefits(data.levelBenefits),
  };
  return parsedData;
};

const getLevelDiscountCreationData = (data) => {
  const discountCombinationFieldMap = {
    [ONLINE_STORE_DISCOUNT_TYPE.ORDER]: "combinedWithOrderDiscount",
    [ONLINE_STORE_DISCOUNT_TYPE.PRODUCT]: "combinedWithProductDiscount",
    [ONLINE_STORE_DISCOUNT_TYPE.DELIVERY]: "combinedWithShippingDiscount",
  }
  const discountCombination = {};
  const inputCombinationIds = data?.discountCombination?.map(item => item?.pk) || [];

  Object.entries(discountCombinationFieldMap).forEach(([discounType, discountCombinationField]) => {
    discountCombination[discountCombinationField] = inputCombinationIds.includes(discounType) || false;
  });

  const isProductTypeDiscount = data?.onlineStoreDiscountType === ONLINE_STORE_DISCOUNT_TYPE.PRODUCT

  const productTypeDiscountLimitation = {
    limitedCollections: isProductTypeDiscount
      ? JSON.stringify(data?.limitedCollections?.map(item => ({
        id: item?.value?.pk,
        title: item?.value?.title,
      })) || [])
      : null,
    limitedProductTags: isProductTypeDiscount
      ? data?.limitedProductTags?.map(item => item?.value?.pk) || []
      : null,
  }

  return {
    id: data.pk,
    discount: Number(data.discountNumber) / 100.0,
    freeDeliveryOnShopify: (discountCombination?.combinedWithShippingDiscount && data.freeDeliveryOnShopify) || false,
    discountType: data?.onlineStoreDiscountType,
    limitedMinSpend: data?.limitedMinSpending || null,
    limitedMinQuantity: data?.limitedMinQuantity || null,
    ...productTypeDiscountLimitation,
    ...discountCombination,
  }
}
export default createModel({
  namespace: 'levelDiscount',
  states: {
    listDisplayFields: [
      { displayName: 'ID', fieldName: 'pk', orderField: 'pk', linked: true },
      {
        displayName: 'Level',
        fieldName: 'name',
      },
      {
        displayName: 'Level discount value',
        fieldName: 'displayDiscount',
      },
      {
        displayName: 'Shopify - free delivery',
        fieldName: 'displayShopifyFreeDelivery',
      },
      { displayName: 'Last modified time', fieldName: 'listDisplayLastModifiedTime' },
    ],
    detail: {
      sourceName: '',
      discount: '',
    },
  },
  reducers: {},
  params: {
    sessionKey: SESSION_KEYS.LEVEL_DISCOUNT_SESSION_KEY,
    dataKey: SESSION_KEYS.LEVEL_DISCOUNT_DATA_SESSION_KEY,
    listAPI: getLevelDiscountList,
    parse: (data) => data?.levelDiscounts.edges.map((item) => parseDetail(item.node)),
    pkNode: 'LevelDiscountNode',
    detailAPI: getLevelDiscount,
    parseDetail: (data) => parseDetail(data.levelDiscount),
    objectKey: 'levelDiscount'
  },
  effects: {
    createOrUpdate: [
      function* ({ payload }, { call, select, put }) {
        console.log("@56", payload)
        const data = getLevelDiscountCreationData(payload);
        const serviceArgs = [updateLevelDiscount, data];
        yield put({
          type: 'responseCreateOrUpdate',
          payload: { serviceArgs, isCreate: false },
        });

        yield put({
          type: 'updateLevelBenefits',
          payload: {
            levelId: payload?.level?.pk,
            levelBenefits: payload?.levelBenefits,
          }
        })

      },
      { type: 'takeLatest' },
    ],

    updateLevelBenefits: [
      function* ({ payload }, { call, select, put }) {
        const updatePayload = {
          levelId: payload?.levelId,
          levelBenefits: cleanLevelBenefits(payload?.levelBenefits),
        };
        const serviceArgs = [updateLevelBenefits, updatePayload];
        function onSuccess(data) {
          console.log("@@updateLevelBenefits", data);
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
  },
});
